import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Formik, Form } from 'formik';
import {
  Box,
  Button,
  Grid,
  Container,
  InputAdornment,
  Typography,
  CircularProgress,
  makeStyles
} from "@material-ui/core";
import { CloudUpload as CloudUploadIcon } from '@material-ui/icons';
import ProductRegisterStyle from "./ProductRegisterStyle";
import PlaceholderImage from "../../assets/placeholder.png"
import { productSchema } from "../../utils/schemas/productSchema";
import FormField from "../FormField/FormField";
import FormFieldChip from "../FormFieldChip/FormFieldChip";
import { Creators as ProdutoActions } from "../../store/ducks/products";
import { notifySuccess, notifyWarning } from "../../utils/js/helpers";
import { COLORS } from "../../utils/js/constants";

const useStyles = makeStyles((theme) => ProductRegisterStyle(theme));

const ProductRegister = ({
       productToEdit,
       produtos,
       uploadImage,
       createProduct,
       onFinished
}) => {
  const [image, setImage] = useState(null)
  const [product, setProduct] = useState(null)
  const [sent, setSent] = useState(false)
  const [loading, setLoading] = useState(false)
  const classes = useStyles();

  useEffect(() => {
    const { type, data } = produtos

    if (type === "products/UPLOAD" && !sent) {
      setSent(true)
      createProduct({ ...product, imagem: data.result })
    }

    if (sent && type === "products/ADD") {
      notifySuccess("Produto salvo!")
      setLoading(false)
      onFinished()
    }
  }, [createProduct, onFinished, product, productToEdit, produtos, sent])

  const handleSubmit = useCallback((product) => {
    if (!image && !productToEdit) {
      notifyWarning("Necessário carregar imagem para o produto.");
      return;
    }

    setLoading(true)
    setProduct(product)

    if (productToEdit && !image) {
      setSent(true)
      createProduct(product)
    } else {
      uploadImage(image, image.name)
    }
  }, [createProduct, image, productToEdit, uploadImage])

  const displayImage = () => {
    if (image) return URL.createObjectURL(image)
    if (productToEdit?.imagem) return productToEdit.imagem;
    return PlaceholderImage;
  }

  const handleImageInput = ({ target }) => {
    const { files } = target
    if (files.length) setImage(files[0])
  }

  const submitBtnTitle = () => !productToEdit ? "Finalizar cadastro" : "Finalizar edição"

  const loadingElement = () => (<>
    <span>Aguarde </span>
    <CircularProgress color={COLORS.white} size={12} />
  </>)

  return (
    <Box className={classes.root}>
      <Typography className={classes.title} variant="h6" component="h2">
        Digite as informações do produto
      </Typography>

      <Formik
        initialValues={{
          ...productSchema.getDefaultFromShape(),
          ...productToEdit
        }}
        validationSchema={productSchema}
        onSubmit={(values) => handleSubmit(values)}
      >{() => (
        <Form>
          <Grid container justify="space-between" spacing={3}>
            <Grid item xs={3}>
              <img className={classes.image} src={displayImage()} alt="product"/>
              <input
                accept="image/*"
                id="contained-button-file"
                style={{display: 'none'}}
                type="file"
                onChange={handleImageInput}
              />
              <label htmlFor="contained-button-file">
                <Button
                  fullWidth
                  variant="contained"
                  className={classes.uploadButton}
                  startIcon={<CloudUploadIcon />}
                  component="span"
                >
                  Adicionar Imagem
                </Button>
              </label>
            </Grid>

            <Grid container spacing={2} item xs={9}>
              <Grid container item direction="column" spacing={2} xs={6}>
                <Grid item>
                  <FormField
                    name="nome"
                    type="text"
                    label="Nome do Produto"
                    variant="outlined"
                    fullWidth
                    required
                  />
                </Grid>

                <Grid item>
                  <FormField
                    name="codigo_fornecedor"
                    type="text"
                    label="Código do Fornecedor"
                    variant="outlined"
                    fullWidth
                    required
                  />
                </Grid>

                <Grid container item justify="space-between" spacing={2}>
                  <Grid item xs={6}>
                    <FormField
                      name="preco"
                      type="number"
                      label="Preço"
                      variant="outlined"
                      min="0"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">R$ </InputAdornment>,
                      }}
                      fullWidth
                      required
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <FormField
                      name="preco_custo"
                      type="number"
                      label="Preço de custo"
                      variant="outlined"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">R$ </InputAdornment>,
                      }}
                      fullWidth
                      required
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item container direction="column" spacing={2} xs={6}>
                <Grid item>
                  <FormField
                    name="codigo"
                    type="text"
                    label="Código do Produto"
                    variant="outlined"
                    fullWidth
                    required
                  />
                </Grid>

                <Grid item>
                  <FormField
                    name="localizacao"
                    type="text"
                    label="Localização"
                    variant="outlined"
                    fullWidth
                    required
                  />
                </Grid>

                <Grid container item justify="space-between" spacing={2}>
                  <Grid item xs={6}>
                    <FormField
                      name="estoque_atual"
                      type="number"
                      label="Estoque atual"
                      variant="outlined"
                      fullWidth
                      required
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <FormField
                      name="estoque_minimo"
                      type="number"
                      label="Estoque mínimo"
                      variant="outlined"
                      fullWidth
                      required
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Grid container item spacing={4} justify="space-between" xs={12}>
              <Grid container item xs={6} className={classes.alignContentFlexStart}>
                <FormFieldChip
                  name="compatibilidade"
                  label="Compatibilidade"
                />
              </Grid>

              <Grid container item xs={6} className={classes.alignContentFlexStart}>
                <FormFieldChip
                  name="segmento"
                  label="Segmento(s)"
                />
              </Grid>
            </Grid>
          </Grid>

          <Container>
            <Button
              className={classes.submitButton}
              type="submit"
              variant="contained"
              disabled={loading}
            >
              {loading ? loadingElement() : submitBtnTitle()}
            </Button>
          </Container>
        </Form>
      )}</Formik>
    </Box>
  );
};

const mapStateToProps = ({ produtos }) => ({ produtos })
const mapDispatchToProps = dispatch => {
  const { uploadImage, createProduct } = ProdutoActions
  return bindActionCreators({
    uploadImage, createProduct
  }, dispatch)
}

export default connect(mapStateToProps, mapDispatchToProps)(ProductRegister);
